import React from "react";
import { DateTime } from "luxon";
import DateTimeView from "./DateTimeView";
import { useBookingDraft } from "../BookingDraftContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { salonNow } from "@/lib/time";
import { useAvailabilityForDates } from "@/hooks/calendar/useAvailabilityForDates";
import WaitlistPanel from "./WaitlistPanel";
import { getWaitlistConfig } from "../../../waitlistConfigStore";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";
import { visibleDateRange } from "../../../lib/calendarRange";

/** helper: seconds -> "HH:mm" using salon tz */
function secToHHmm(dateISO: string | null, sec: number, zone: string) {
  if (!dateISO) return "";
  return DateTime.fromISO(dateISO, { zone }).startOf("day").plus({ seconds: sec }).toFormat("HH:mm");
}

type TimeSlot = {
  id: string;
  label: string;
  startISO: string;
  startSec: number;
  staffId: number;
  lastHour?: { discount_pct: number; regular_price: number; price: number };
};

export default function DateTimeViewContainer({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const { draft, actions } = useBookingDraft();
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC"; // salon TZ
  const now = salonNow();                          // salon "now"
  const todayISO = now.toISODate();
  const nowSec = Math.floor(now.diff(now.startOf("day")).as("seconds"));

  // --- Local (container) month state, starts from either selected date or 'today'
  const initial = draft.dateISO ?? todayISO;
  const [monthStart, setMonthStart] = React.useState(
    DateTime.fromISO(initial, { zone: tz }).startOf("month")
  );
  // --- The dates of the visible grid (the month ± the neighbouring days it shows), from today
  const datesInRange = React.useMemo(
    () => visibleDateRange(monthStart, todayISO, tz),
    [monthStart.toISO(), todayISO, tz]
  );

  // --- Only fetch when we have specialist + services (per requirement)
  const enabled = !!draft.specialistId && (draft.serviceIds?.length ?? 0) > 0;

  const masterId = draft.specialistId && draft.specialistId !== "any" ? Number(draft.specialistId) : undefined;
  const serviceIdsNum = React.useMemo(
    () => (draft.serviceIds ?? []).map(Number).filter((id) => Number.isFinite(id)),
    [draft.serviceIds]
  );

  // Filter out zero-length services for availability. If everything is zero-length,
  // fall back to master-only availability so dates are still shown.
  const availabilityServiceIds = React.useMemo(() => {
    if (!serviceIdsNum.length) return [];
    const reg = getServiceRegistry();
    return serviceIdsNum.filter((id) => {
      const descriptor =
        reg.getServiceById?.(id) ||
        reg.getSnapshot?.()?.services?.[id];
      if (!descriptor) return true; // keep unknowns to avoid dropping valid IDs
      const rawCustom =
        masterId && descriptor.staffDurationSeconds
          ? descriptor.staffDurationSeconds[masterId]
          : undefined;
      const custom = Number(rawCustom);
      if (Number.isFinite(custom) && custom > 0) return true;
      const def = Number(descriptor.duration_seconds);
      return Number.isFinite(def) && def > 0;
    });
  }, [serviceIdsNum, masterId]);

  const { data: availByDate, isLoading, error } = useAvailabilityForDates(datesInRange, {
    masters: masterId ? [masterId] : undefined,
    services: availabilityServiceIds.length ? availabilityServiceIds : undefined,
    enabled,
  });

  // --- Which dates are actually bookable (has any future slot for the chosen master)
  const enabledDates = React.useMemo(() => {
    const set = new Set<string>();
    if (!availByDate) return set;

    for (const date of datesInRange) {
      const rows = availByDate[date] ?? [];
      // Filter by selected master (if provided)
      const rowsForMaster = masterId ? rows.filter(r => r.staffId === masterId) : rows;
      // For today: drop past slots
      const futureSlots = rowsForMaster.flatMap(r => r.slots)
        .filter(s => date !== todayISO || (s.start ?? 0) >= nowSec);

      if (futureSlots.length > 0) set.add(date);
    }
    return set;
  }, [availByDate, datesInRange, masterId, todayISO, nowSec]);

  // --- Nearest available date >= today, within the loaded range
  const nearestAvailableDateISO = React.useMemo(
    () => datesInRange.find((iso) => iso >= todayISO && enabledDates.has(iso)) ?? null,
    [enabledDates, datesInRange, todayISO]
  );

  // Auto-select today on first load if it has any future slots and no date picked yet
  React.useEffect(() => {
    if (!enabled || isLoading || error) return;
    if (draft.dateISO) return;
    if (enabledDates.has(todayISO)) {
      actions.setDateISO(todayISO);
      actions.setStartISO(null); // ensure a clean time selection
    }
  }, [enabled, isLoading, error, draft.dateISO, enabledDates, todayISO, actions]);

  // --- Build timeSlots for the currently picked date
  const timeSlots = React.useMemo(() => {
    if (!draft.dateISO || !availByDate) return [];
    const rows = availByDate[draft.dateISO] ?? [];
    const rowsForMaster = masterId ? rows.filter(r => r.staffId === masterId) : rows;

    const allSlots: TimeSlot[] = [];
    rowsForMaster.forEach(r => {
      r.slots.forEach((s, slotIndex) => {
        if (draft.dateISO === todayISO && (s.start ?? 0) < nowSec) return; // skip past slots
        const label = s.startStr ?? secToHHmm(draft.dateISO, s.start, tz);
        const startISO = DateTime.fromISO(draft.dateISO!, { zone: tz }).startOf("day").plus({ seconds: s.start }).toUTC().toISO();
        const id = `${draft.dateISO!}::${r.staffId}::${s.start}::${slotIndex}`;
        allSlots.push({
          id,
          label,
          startISO,
          startSec: s.start,
          staffId: r.staffId,
          lastHour: s.lastHour,
        });
      });
    });

    return allSlots.sort((a, b) => a.startSec - b.startSec);
  }, [availByDate, draft.dateISO, masterId, todayISO, nowSec, tz]);

  // --- Compute selectedSlotId by matching draft.startISO
  const selectedSlotId = React.useMemo(() => {
    if (!draft.startISO) return null;
    return timeSlots.find(slot => slot.startISO === draft.startISO)?.id ?? null;
  }, [draft.startISO, timeSlots]);

  // --- Pickers
  const handlePickDate = (dateISO: string) => {
    if (!enabledDates.has(dateISO)) return; // guard
    actions.setDateISO(dateISO);
    actions.setStartISO(null);
    actions.setLastHour(null);
  };

  const handlePickSlot = (slotId: string) => {
    const slot = timeSlots.find(s => s.id === slotId);
    if (slot) {
      actions.setStartISO(slot.startISO);
      actions.setLastHour(
        slot.lastHour
          ? {
              discountPct: slot.lastHour.discount_pct,
              price: slot.lastHour.price,
              regularPrice: slot.lastHour.regular_price,
            }
          : null
      );
    }
  };

  const handleGoToNearest = () => {
    if (nearestAvailableDateISO) handlePickDate(nearestAvailableDateISO);
  };

  // --- Waiting-list panel triggers (waitlist README §4.1, client-side):
  //     horizon: earliest bookable date further than horizon_days away (or
  //     none in the loaded range); day: picked date has zero slots.
  const wlConfig = getWaitlistConfig();
  const wlServiceId = serviceIdsNum.length === 1 ? serviceIdsNum[0] : null;
  const waitlistNode = React.useMemo(() => {
    if (!wlConfig?.active || !enabled || isLoading || error || !wlServiceId) return null;
    const horizonISO = DateTime.fromISO(todayISO, { zone: tz })
      .plus({ days: wlConfig.horizon_days }).toISODate();
    const horizonTrigger =
      nearestAvailableDateISO === null || nearestAvailableDateISO > horizonISO;
    // day trigger: a PICKED date whose slots are gone (disabled dates
    // can't be picked, so the enabledDates check was unreachable)
    const dayTrigger = !!draft.dateISO && timeSlots.length === 0;
    if (!horizonTrigger && !dayTrigger) return null;
    const from = dayTrigger && draft.dateISO ? draft.dateISO : todayISO;
    const to = dayTrigger && draft.dateISO
      ? draft.dateISO
      : DateTime.fromISO(todayISO, { zone: tz }).plus({ days: 30 }).toISODate();
    return (
      <WaitlistPanel
        serviceId={wlServiceId}
        staffId={masterId ?? null}
        defaultFrom={from}
        defaultTo={to}
        collapsible
      />
    );
  }, [wlConfig, enabled, isLoading, error, wlServiceId, nearestAvailableDateISO,
      draft.dateISO, timeSlots, todayISO, tz, masterId]);

  return (
    <DateTimeView
      // calendar control
    monthJSDate={new Date(monthStart.year, monthStart.month - 1, 1)}
    onMonthChange={(jsDate: Date) => {
      // derive by fields, not by interpreting the absolute instant
      const y = jsDate.getFullYear();
      const m = jsDate.getMonth() + 1; // 1-12
      setMonthStart(DateTime.fromObject({ year: y, month: m, day: 1 }, { zone: tz }));
    }}
      // date selection
      dateISO={draft.dateISO}
      onPickDate={handlePickDate}
      // enabling
      enabledDateISOSet={enabledDates}
      // times
      timeSlots={timeSlots}
      selectedSlotId={selectedSlotId}
      onPickSlot={handlePickSlot}
      // today empty-state
      showNoSlotsToday={enabled && !enabledDates.has(todayISO)}
      onGoToNearestDate={handleGoToNearest}
      // waiting list (README §4.1)
      waitlistPanel={waitlistNode}
      // plumbing
      loading={isLoading}
      error={!!error}
      onBack={onBack}
      onDone={onDone}
    />
  );
}
